<script setup lang="ts">
/**
 * Fully controlled: the consumer owns `isOpen` and reacts to `close`. The only
 * close trigger built in here is outside-scroll (see `onScroll`).
 *
 * Click-outside is deliberately NOT implemented. A generic document-level
 * outside-click closer would race with how consumers select inside the popover:
 * the input components (NxSelectInput, NxAutocompleteInput, NxTimeInput,
 * NxDateInput) lean on `@mousedown.prevent` ordering so an option is picked
 * before focus/close fires. A built-in pointerdown/mousedown closer would fire
 * first and swallow the selection, and a built-in `click` closer would still
 * double up with — and have to stay in sync with — the bespoke
 * mousedown/blur/focusout logic each of those consumers already runs. So each
 * consumer owns its own dismiss behavior and emits `close` itself; keeping
 * that policy out of this primitive avoids the cross-component coupling.
 */
import { nextTick, onUnmounted, ref, useId, watch } from 'vue';
import { containOverlayWheel } from '../lib/containOverlayWheel';
import { useTableFocusClaim } from '../Table/focusScope';

export type NxDropdownProps = {
  isOpen: boolean;
  /** Defaults to `start` */
  align?: 'start' | 'center' | 'end';
  /** Defaults to `false` */
  fitWidth?: boolean;
};

const props = withDefaults(defineProps<NxDropdownProps>(), {
  align: 'start',
  fitWidth: false,
});

const emit = defineEmits<{ close: [] }>();

const anchorId = `--dropdown-anchor-${useId()}`;
const popoverRef = ref<HTMLElement | null>(null);
// Opened from a table, it belongs to that table's editing wherever it renders.
useTableFocusClaim(popoverRef, () => props.isOpen);

function onScroll(e: Event) {
  // Ignore scrolls inside the dropdown itself (e.g. long option list)
  if (popoverRef.value?.contains(e.target as Node)) return;
  emit('close');
}

watch(
  () => props.isOpen,
  (open) => {
    nextTick(() => {
      const el = popoverRef.value;
      if (open) {
        if (el && 'showPopover' in el) {
          try {
            el.showPopover();
          } catch {
            /* already open */
          }
        }
        // Defer so programmatic scroll (e.g. consumer calling scrollIntoView on open) doesn't trigger close
        if (props.isOpen) {
          window.addEventListener('scroll', onScroll, true);
        }
      } else {
        if (el && 'hidePopover' in el) {
          try {
            el.hidePopover();
          } catch {
            /* already closed */
          }
        }
        window.removeEventListener('scroll', onScroll, true);
      }
    });
  },
  { immediate: true }
);

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll, true);
});
</script>

<template>
  <div class="nx-dropdown-anchor" :style="{ 'anchor-name': anchorId }">
    <slot name="trigger" />
    <div
      v-show="isOpen"
      ref="popoverRef"
      popover="manual"
      :class="[
        'nx-dropdown-popover',
        {
          'nx-dropdown-popover--start': props.align === 'start',
          'nx-dropdown-popover--center': props.align === 'center',
          'nx-dropdown-popover--end': props.align === 'end',
          'nx-dropdown-popover--fit': props.fitWidth,
        },
      ]"
      :style="{ 'position-anchor': anchorId }"
      @wheel="(e: WheelEvent) => containOverlayWheel(e, popoverRef)"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped>
.nx-dropdown-anchor {
  width: 100%;
  position: relative;
}
</style>

<style>
@position-try --nxt-dropdown-flip-block {
  top: auto;
  bottom: anchor(top);
  margin-block: 0 var(--nxt-space-2xs);
}

.nx-dropdown-popover {
  position: fixed;
  position-try-fallbacks: --nxt-dropdown-flip-block;
  top: anchor(bottom);
  width: anchor-size(width);
  margin: var(--nxt-space-2xs) 0 0;
  padding: 0;
  border: none;
  background: transparent;
  overflow: visible;
  overscroll-behavior: contain;
  color: var(--nxt-fg);
}

/**
 * Transparent hover bridges that span the 5px margin between the trigger and the popover, so
 * the cursor can cross the visual gap without `mouseleave` firing on the popover. Both ::before
 * (top side, used when the popover is rendered below the trigger) and ::after (bottom side,
 * used when `--nxt-dropdown-flip-block` flipped the popover above the trigger) are always
 * present — the bridge in the "wrong" direction is a harmless 5px tolerance zone past the
 * popover content.
 */
.nx-dropdown-popover::before,
.nx-dropdown-popover::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: var(--nxt-space-2xs);
}

.nx-dropdown-popover::before {
  top: calc(-1 * var(--nxt-space-2xs));
}

.nx-dropdown-popover::after {
  bottom: calc(-1 * var(--nxt-space-2xs));
}

.nx-dropdown-popover--fit {
  /* Content-sized, but never narrower than the trigger (a select in a slim
     table column must not clip its option labels) and capped so a runaway
     label cannot span the viewport. anchor-size() gracefully drops where
     unsupported, leaving plain fit-content. */
  width: fit-content;
  min-width: anchor-size(width);
  max-width: min(90vw, var(--nxt-panel-width-3xl));
}

.nx-dropdown-popover--start {
  left: anchor(left);
}
.nx-dropdown-popover--center {
  left: anchor(center);
  transform: translateX(-50%);
}
.nx-dropdown-popover--end {
  right: anchor(right);
  left: unset !important;
}
</style>
